import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spinner } from "reactstrap";
import axios from "axios";
import { _imagesURL, adminBaseURL } from "../../constants/API";
import { NEW_CONTEST } from "../../constants/RoutesUrl";
import { Sponsorpost } from "../../App";
import "./mentorship.css";

const AUTO_SLIDE_MS = 3000;
const TRANSITION_MS = 700;

// MentorshipGrid.jsx's /user/mentor/home fetch stashes ads.mentor_center
// here — this banner is that slot; ads.mentor_left is MentorshipAdCard's.
// Each entry wraps the real ad under `general_ad` (media is a relative path).
function getBannerSlides() {
  try {
    const ads = JSON.parse(localStorage.getItem("mr_home_data"))?.ads?.mentor_center;
    if (!Array.isArray(ads) || !ads.length) return [];
    return ads
      .map((slot) => slot.general_ad)
      .filter((ad) => ad?.media)
      .map((ad) => ({
        ...ad,
        src: `${_imagesURL}${ad.media}`,
        media_size: parseFloat(ad.media_size) || (998 / 349),
      }));
  } catch (e) {
    return [];
  }
}

// mr_home_data isn't written until MentorshipGrid.jsx's fetch resolves —
// this key's mere presence (regardless of ad content) is what tells us
// that first fetch has actually happened at least once.
function hasHomeDataLoaded() {
  return localStorage.getItem("mr_home_data") !== null;
}

// Same internal_model switch as MentorshipAdCard.jsx's handleRedirect.
function handleRedirect(navigate, ad) {
  switch (ad.internal_model) {
    case "course":
      navigate(`/course-detail/${ad.course_id}`);
      break;
    case "profile":
      navigate(`/profile/${ad.user_id}`);
      break;
    case "clinical_session":
      navigate(`/session-details/${ad.post_id}`);
      break;
    case "webinar":
      navigate(`/webinar-details/${ad.post_id}`);
      break;
    case "clinical_study":
      navigate(`/post/${ad.post_id}`);
      break;
    case "event":
      navigate(`/event-details/${ad.event_id}`);
      break;
    default:
      break;
  }
}

// Mirrors MentorshipAdCard.jsx's ImageComponent (src/components/common/
// ImageComponent.jsx) — the one image-loading approach in this app that's
// actually held up after a backgrounded tab (switching to another app,
// another window, a Google search, etc.), unlike this banner's old
// hand-rolled onLoad/ref/onError handling on a live DOM <img>. The
// difference: this preloads through a plain, *off-DOM* `new Image()` object
// in its own effect, and only swaps the visible <img>'s src once that
// preload's own onload actually fires. A DOM-attached <img> mid-request can
// get silently suspended by the browser when the tab backgrounds and never
// fire load OR error once it's foregrounded again — a fresh, disposable
// Image() object created in a brand new effect run doesn't inherit that
// stuck state the same way.
function BannerSlideImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  const [displaySrc, setDisplaySrc] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    const preload = new Image();
    preload.src = src;
    preload.onload = () => {
      if (cancelled) return;
      setDisplaySrc(src);
      setLoaded(true);
    };
    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <>
      {!loaded && <Spinner style={{ color: "#1dc1dd" }} />}
      <img
        src={displaySrc}
        alt={alt}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          display: "block",
          objectFit: "cover",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.25s ease",
        }}
      />
    </>
  );
}

export default function MentorshipBanner() {
  // Switching away to another window/app and back leaves this specific
  // sliding track blank sometimes, even with valid ad data and fully
  // loaded images sitting right underneath — a known class of mobile/
  // browser bug where the GPU compositor layer for a static
  // `transform: translateX(...)` element doesn't get properly repainted
  // once the tab is backgrounded and foregrounded again. MentorshipAdCard
  // never hits this because it animates through react-responsive-carousel
  // instead of a bare CSS transform. Forcing a full remount (via this key,
  // bumped on visibilitychange) sidesteps the whole class of problem —
  // every image and the transform layer both get freshly recomputed from
  // scratch, rather than trying to patch whichever specific piece the
  // browser left stale.
  const [visibilityKey, setVisibilityKey] = useState(0);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setVisibilityKey((k) => k + 1);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  return <MentorshipBannerInner key={visibilityKey} />;
}

function MentorshipBannerInner() {
  const navigate = useNavigate();
  const sponsorContext = useContext(Sponsorpost);
  const updateContext = sponsorContext?.updateContext;
  const [dataReady, setDataReady] = useState(hasHomeDataLoaded);
  const slides = getBannerSlides();
  const [index, setIndex] = useState(0);
  const [withTransition, setWithTransition] = useState(true);
  const timerRef = useRef(null);

  // Same click handling as MentorshipAdCard.jsx / AgendaPromoBanner.jsx —
  // external links get an https:// prefix when missing one, internal ads
  // route via handleRedirect, everything else falls back to the dynamic
  // contest page. Tracking (user/ads/view) is non-blocking and silently
  // skipped when there's no token, instead of bouncing the click to LOGIN.
  const handleAdClick = (ad) => {
    if (!ad) return;
    const targetLink = ad.link;

    if (
      ad.type === "external" ||
      (targetLink &&
        (targetLink.startsWith("http://") ||
          targetLink.startsWith("https://") ||
          targetLink.includes("www.")))
    ) {
      const url =
        targetLink.startsWith("http://") || targetLink.startsWith("https://")
          ? targetLink
          : `https://${targetLink}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } else if (ad.type === "internal") {
      handleRedirect(navigate, ad);
    } else {
      navigate(NEW_CONTEST, { state: { dynamicAd: ad } });
    }
    if (typeof updateContext === "function") updateContext(ad);

    try {
      const signupData = JSON.parse(localStorage.getItem("SignupData") || "{}");
      const token = signupData?.data?.token;
      if (token && ad?.id) {
        axios
          .post(
            `${adminBaseURL}user/ads/view`,
            { device: "Web", ad_id: ad.id, type: "view" },
            { headers: { Authorization: `Bearer ${token}` } }
          )
          .catch(() => {});
      }
    } catch (err) {
      // Analytics non-blocking
    }
  };

  // MentorshipGrid.jsx dispatches this the moment mr_home_data is written —
  // shows the shimmer until then instead of just rendering nothing.
  useEffect(() => {
    if (dataReady) return undefined;
    const handleUpdate = () => setDataReady(true);
    window.addEventListener("mrHomeDataUpdated", handleUpdate);
    return () => window.removeEventListener("mrHomeDataUpdated", handleUpdate);
  }, [dataReady]);

  // Clone the first slide onto the end so the track can keep sliding
  // forward and then "teleport" back to slide 0 without ever reversing.
  const track = [...slides, slides[0]];

  const goTo = (i) => {
    setWithTransition(true);
    setIndex(i);
  };

  useEffect(() => {
    if (slides.length < 2) return undefined;
    timerRef.current = setInterval(() => {
      setWithTransition(true);
      setIndex((prev) => prev + 1);
    }, AUTO_SLIDE_MS);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  // After sliding onto the cloned (duplicate) first slide, snap back to
  // the real slide 0 with no transition, so it looks like an endless loop.
  const handleTransitionEnd = () => {
    if (index === slides.length) {
      setWithTransition(false);
      setIndex(0);
    }
  };

  const firstSlide = slides[0];
  const bannerRatio = firstSlide?.media_size || (998 / 349);

  if (!dataReady) {
    return (
      <div
        className="position-relative overflow-hidden w-100"
        style={{
          borderRadius: "20px",
          lineHeight: 0,
          width: "100%",
          aspectRatio: `${bannerRatio}`,
        }}
      >
        <style>{`
          .mentorship-shimmer-block {
            background: linear-gradient(90deg, #F1F5F9 0%, #E2E8F0 50%, #F1F5F9 100%);
            background-size: 200% 100%;
            animation: mrShimmerWave 1.6s ease-in-out infinite;
          }
          @keyframes mrShimmerWave {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }
        `}</style>
        <div className="mentorship-shimmer-block" style={{ width: "100%", height: "100%" }} />
      </div>
    );
  }

  if (!slides.length) return null;

  const activeDot = index % slides.length;

  return (
    <div
      className="position-relative overflow-hidden w-100"
      style={{
        borderRadius: "20px",
        lineHeight: 0,
        width: "100%",
        aspectRatio: `${bannerRatio}`,
      }}
    >
      {/* Track */}
      <div
        onTransitionEnd={handleTransitionEnd}
        style={{
          display: "flex",
          height: "100%",
          width: `${track.length * 100}%`,
          transform: `translateX(-${index * (100 / track.length)}%)`,
          transition: withTransition
            ? `transform ${TRANSITION_MS}ms cubic-bezier(0.65, 0, 0.35, 1)`
            : "none",
        }}
      >
        {track.map((slide, i) => {
          const isClickable =
            Boolean(slide?.link) ||
            slide?.type === "dynamic" ||
            slide?.type === "internal" ||
            slide?.type === "external";
          return (
          <div
            key={i}
            className="position-relative d-flex align-items-center justify-content-center"
            style={{
              flex: `0 0 ${100 / track.length}%`,
              width: "100%",
              height: "100%",
              background: "#F7FDFF",
              cursor: isClickable ? "pointer" : "default",
            }}
            onClick={isClickable ? () => handleAdClick(slide) : undefined}
          >
            <BannerSlideImage
              src={slide?.src || slide}
              alt={`Mentorship banner slide ${(i % slides.length) + 1}`}
            />
          </div>
          );
        })}
      </div>

      {/* Minimal Animated Dot Indicators matching Courses and Agenda banners */}
      {slides.length > 1 && (
        <div className="cr-banner-dots">
          {slides.map((_, i) => (
            <div
              key={i}
              onClick={(e) => {
                e.stopPropagation();
                goTo(i);
              }}
              className={`cr-banner-dot${activeDot === i ? " active" : ""}`}
              style={{ width: activeDot === i ? "24px" : "6px" }}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}